import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from '../../../../CommonComponents/OPL/Modal/Modal';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Tab, TabList } from '../../../../CommonComponents/OPL/Tab';
import {
  selectIsUserClient,
  showTransactionsSelector,
} from '../../../../CommonSelectors/CommonAreaSelector';
import { AchRelationshipDTO } from '../../api/useAchRelationshipsQuery';
import { useNavigateToTransactions } from '../../hooks/useNavigateToTransactions';
import { ErrorModal } from '../ErrorModal/ErrorModal';
import { LoadingModal } from '../LoadingModal/LoadingModal';
import { LinkAccountsModal } from '../ManageAccounts/components/LinkAccountsModal/LinkAccountsModal';
import { UnlinkAccountModal } from '../ManageAccounts/components/UnlinkAccountModal/UnlinkAccountModal';
import { ManageAccounts } from '../ManageAccounts/ManageAccounts';
import { useManageAccounts } from '../ManageAccounts/useManageAccounts';
import { StatusScreen } from '../StatusScreen/StatusScreen';
import { WireInstructions } from '../WireInstructions/WireInstructions';
import { ModalType } from './TransferModal.types';
import { useRouteParams } from '../../../../AccountPerformanceReporting/hooks/useRouteParams';
import { TabSlug } from '../../../../AccountPerformanceReporting/types';
import { StyledTabPanel, StyledTabs } from '../../TransferMoney.styles';

interface ChildrenProps {
  onSuccess(isScheduled?: boolean): void;
  onClose(): void;
  onBack(): void;
}

export enum TabType {
  Deposit = 'ACH Deposit',
  Withdraw = 'ACH Withdraw',
}

interface TransferModalProps {
  tab: TabType;
  onClose(): void;
  onBack(): void;
  children(props: ChildrenProps): React.ReactElement;
}

export const TransferModal: React.FC<TransferModalProps> = ({ tab, children, onClose, onBack }) => {
  const { isLoading, isError, refetch } = useManageAccounts();

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [activeModal, setActiveModal] = useState<ModalType | null>(null);

  const [selectedAccount, setSelectedAccount] = useState<AchRelationshipDTO | null>(null);

  const { navigateToTransactions } = useNavigateToTransactions();

  const { tabSlug } = useRouteParams();

  const isClient = useSelector(selectIsUserClient);

  const showTransactions = useSelector(showTransactionsSelector);

  if (isLoading) {
    return <LoadingModal />;
  }

  if (isError) {
    return <ErrorModal onClose={onClose} onRetry={refetch} />;
  }

  return (
    <>
      {activeModal === null && (
        <Modal isOpen onRequestClose={onClose}>
          <ModalHeader>Transfer Money</ModalHeader>
          <ModalCloseButton onClick={onClose} />

          <StyledTabs selectedIndex={activeTabIndex} onSelect={index => setActiveTabIndex(index)}>
            <TabList px="16">
              <Tab>{tab}</Tab>
              {isClient && <Tab>Manage Accounts</Tab>}
              {tab === TabType.Deposit && <Tab>Wire Instructions</Tab>}
            </TabList>

            <StyledTabPanel>
              {children({
                onSuccess: isScheduled =>
                  setActiveModal(
                    isScheduled ? ModalType.ScheduledSuccessTransfer : ModalType.SuccessTransfer,
                  ),
                onClose,
                onBack,
              })}
            </StyledTabPanel>

            {isClient && (
              <StyledTabPanel>
                <ManageAccounts
                  onBack={onBack}
                  setActiveModal={setActiveModal}
                  setSelectedAccount={setSelectedAccount}
                />
              </StyledTabPanel>
            )}

            {tab === TabType.Deposit && (
              <StyledTabPanel>
                <WireInstructions />
              </StyledTabPanel>
            )}
          </StyledTabs>
        </Modal>
      )}

      {activeModal === ModalType.DeleteAccount && selectedAccount !== null && (
        <UnlinkAccountModal
          onClose={() => {
            setActiveModal(null);
            setSelectedAccount(null);
          }}
          account={selectedAccount}
        />
      )}

      {activeModal === ModalType.SelectAccount && (
        <LinkAccountsModal
          onClose={() => {
            setActiveModal(null);
          }}
        />
      )}

      {activeModal === ModalType.SuccessTransfer && (
        <StatusScreen
          title="Transfer Completed"
          status="success"
          action={
            showTransactions
              ? {
                  label: 'View Pending Transaction',
                  onClick: tabSlug === TabSlug.Activity ? onClose : navigateToTransactions,
                }
              : undefined
          }
          onClose={onClose}
        >
          {showTransactions
            ? 'Pending transactions will show in the transactions page of your account'
            : undefined}
        </StatusScreen>
      )}

      {activeModal === ModalType.ScheduledSuccessTransfer && (
        <StatusScreen
          title="Transfer Scheduled"
          status="success"
          action={{
            label: 'View Scheduled Transfer',
            onClick: tabSlug === TabSlug.Activity ? onClose : navigateToTransactions,
          }}
          onClose={onClose}
        >
          Scheduled transfer will show in the transactions page of your account
        </StatusScreen>
      )}
    </>
  );
};
